import { FormikProvider, useFormik } from 'formik';
import React, { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { TaskKeysType, TextTask as TextTaskType, WorkType } from 'shared/types';
import { TaskName } from 'shared/ui';

import { audioApi, pdfApi, wordApi, taskApi, videoApi } from '../../../api';
import { imgApi } from '../../../api/img-api';
import { textTaskSchema } from '../../../shared/types/task';
import TaskWrapper from '../TaskWrapper';
import InputPoint from 'Components/InputPoint/InputPoint';

export type TextTaskProps = TextTaskType & {
  handleDelete: (id: string) => void;
  userType: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  currentICLId: string;
  updateICL: boolean;
  workType?: WorkType;
  index: number;
  points?: number;
};

function TextTask({
  id,
  handleDelete,
  completedAt,
  text,
  subText,
  handleChangeTaskByKey,
  hasAnswer,
  hasStar,
  indexNumber,
  isActive,
  createdAt,
  type,
  currentICLId,
  updateICL,
  hasPhoto,
  deadline,
  audioUrl,
  videoUrl,
  pdfUrl,
  wordUrl,
  taskPhotos,
  answersPhotos,
  editedAnswerPhotos,
  workType,
  studentPdfUrl,
  studentWordUrl,
  index,
  points,
}: TextTaskProps) {
  const isExam = workType === WorkType.exam;
  const { t } = useTranslation();

  const [taskId, setTaskId] = useState<string>('');
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const task = await taskApi.getTask(currentICLId, id);

        if (task) {
          setTaskId(task.id);
        }
      } catch (error) {
        console.log(error);
      }
    };

    init(); // to check if task exists in Firestore
  }, []);

  const initialFormValues = {
    text: updateICL ? text.replace(/<br>/g, '\n') : '',
    subText: updateICL ? subText?.replace(/<br>/g, '\n') : '',
    id,
    indexNumber,
    isActive,
    createdAt,
    type,
    hasAnswer,
    hasStar,
    hasPhoto,
  };

  const formValues = isExam ? { ...initialFormValues, points: 0 } : initialFormValues;

  const formik = useFormik({
    initialValues: formValues,
    onSubmit: async values => {
      const textWithLB = values.text.replace(/\n/g, '<br>');
      const subTextWithLB = values?.subText?.replace(/\n/g, '<br>');

      if (!taskId) {
        await taskApi
          .addTask<TextTaskType>(currentICLId, {
            ...values,
            indexNumber,
            text: textWithLB,
            subText: subTextWithLB,
            completedAt,
          })
          .then(id => {
            setTaskId(id);
            toast.success(t('Successfully added!'));

            return id;
          });
      } else {
        await taskApi.updateTask<TextTaskType>(currentICLId, {
          ...values,
          text: textWithLB,
          subText: subTextWithLB,
          completedAt,
        });
        toast.success(t('Successfully edited!'));
      }
    },
    validationSchema: textTaskSchema,
  });

  const deleteTask = async () => {
    if (!taskId) {
      handleDelete(id);
    }

    if (updateICL || taskId) {
      handleDelete(id);

      setIsRemovingLoading(true);
      wordUrl && (await wordApi.removeWord(wordUrl, currentICLId, id));
      pdfUrl && (await pdfApi.removePdf(pdfUrl, currentICLId, id));
      studentWordUrl && (await wordApi.removeWord(studentWordUrl, currentICLId, id, true));
      studentPdfUrl && (await pdfApi.removePdf(studentPdfUrl, currentICLId, id, true));
      videoUrl && (await videoApi.removeVideo(videoUrl, currentICLId, id));
      audioUrl && (await audioApi.removeAudio(audioUrl, currentICLId, id));
      taskPhotos &&
        taskPhotos.forEach(async photo => await imgApi.removeImg(photo, currentICLId, id, taskPhotos, 'taskPhotos'));
      answersPhotos &&
        answersPhotos.forEach(
          async photo => await imgApi.removeImg(photo, currentICLId, id, answersPhotos, 'answersPhotos'),
        );
      editedAnswerPhotos &&
        editedAnswerPhotos.forEach(
          async photo => await imgApi.removeImg(photo, currentICLId, id, editedAnswerPhotos, 'editedAnswerPhotos'),
        );
      await taskApi.removeTask<TextTaskType>(currentICLId, id);
      setIsRemovingLoading(false);
    }
  };

  const handleChangeTaskByKeyWithFormik = (id: string, key: TaskKeysType, value: string | boolean | string[]) => {
    handleChangeTaskByKey(id, key, value);

    formik.setFieldValue(key, value);
  };

  return (
    <TaskWrapper
      index={index}
      id={id}
      handleDelete={deleteTask}
      completedAt={completedAt}
      handleChangeTaskByKey={handleChangeTaskByKeyWithFormik}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      save={formik.submitForm}
      formikProp={formik}
      updateICL={updateICL}
      isDeleteButtonDisabled={isRemovingLoading}
      hasPhoto={hasPhoto}
      deadline={deadline}
      isActive={isActive}
      currentICLId={currentICLId}
      audioUrl={audioUrl}
      remoteTaskId={taskId}
      videoUrl={videoUrl}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      taskPhotos={taskPhotos}
      workType={workType}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
    >
      <FormikProvider value={formik}>
        <div className="!mb-4 lg:!mb-5">
          <TaskName />
        </div>

        {isExam && (
          <InputPoint value={formik?.values?.points} onChange={value => formik.setFieldValue('points', value)} />
        )}

        <div className="relative !mb-4 lg:!mb-5">
          <div className="text-[14px] font-bold uppercase lg:text-[18px]">{t('exercise heading')}:</div>

          <textarea
            value={formik.values.text}
            className={
              formik.errors.text
                ? 'h-[83px] w-full rounded-[20px] border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#FA6238_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
                : 'h-[83px] w-full rounded-[20px] border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
            }
            onChange={event => formik.setFieldValue('text', event.target.value)}
            placeholder={t('enter task title')}
          />

          {!!formik.errors.text && (
            <p className="absolute left-0 top-14 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[60px]">
              {t(formik.errors.text)}
            </p>
          )}
        </div>

        <div className="relative">
          <div className="text-[14px] font-bold uppercase lg:text-[18px]">{t('exercise')}:</div>

          <textarea
            value={formik.values.subText}
            className={`h-[83px] w-full rounded-[20px] border-b-2 text-[18px] lg:text-[22px] ${formik.errors.subText ? 'border-red-600' : 'border-black'} border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] font-medium leading-[35px] placeholder:text-lg lg:placeholder:text-[22px]`}
            onChange={event => formik.setFieldValue('subText', event.target.value)}
            placeholder={t('enter task text')}
          />

          {!!formik.errors.subText && (
            <p className="absolute -bottom-4 left-0 !mb-0 inline-block h-fit text-xs text-red-600">
              {t(formik.errors.subText)}
            </p>
          )}
        </div>
      </FormikProvider>
    </TaskWrapper>
  );
}

export default memo(TextTask);
